import { ChangeTracker } from '../changes/ChangeTracker';
import { RegistrationSnapshot, TrackableObject } from '../changes/DomainChange';
import { newRegistrationId, RegistrationId } from '../changes/ids';
import { GenerationModelDetails } from '../dicom/dicomBase';
import { DICOMApprovalStatus } from '../dicom/dicomConstants';
import { DicomRegistration } from '../dicom/dicomRegistration';
import { ApprovedRegistrationError } from '../errors/DomainError';

export type RegistrationKind = 'rigid' | 'deformable';

export abstract class ApprovableRegistration implements TrackableObject {
    readonly trackableKind = 'registration' as const;

    abstract readonly registrationKind: RegistrationKind;
    readonly id: RegistrationId = newRegistrationId();
    protected tracker: ChangeTracker | null = null;
    public readonly sourceFrameOfReferenceUid: string;
    public readonly targetFrameOfReferenceUid: string;
    public readonly dicomParent: DicomRegistration;

    private _approvalStatus: DICOMApprovalStatus =
        DICOMApprovalStatus.UNAPPROVED;

    private _reviewerName = '';

    constructor(
        sourceFrameOfReferenceUid: string,
        targetFrameOfReferenceUid: string,
        parent: DicomRegistration
    ) {
        this.sourceFrameOfReferenceUid = sourceFrameOfReferenceUid;
        this.targetFrameOfReferenceUid = targetFrameOfReferenceUid;
        this.dicomParent = parent;
    }

    get trackableId(): RegistrationId {
        return this.id;
    }

    attachTracker(tracker: ChangeTracker): void {
        this.tracker = tracker;
    }

    protected snapshotFields(): Omit<
        RegistrationSnapshot,
        'transformRevision'
    > {
        return {
            id: this.id,
            sopInstanceUid: this.sopInstanceUid,
            contentLabel: this.contentLabel,
            contentDescription: this.contentDescription,
            contentCreatorName: this.contentCreatorName,
            contentDate: this.contentDate,
            sourceFrameOfReferenceUid: this.sourceFrameOfReferenceUid,
            targetFrameOfReferenceUid: this.targetFrameOfReferenceUid,
            approvalStatus: this.approvalStatus,
            reviewerName: this._reviewerName,
            isApproved: this.isApproved,
            isNew: this.isNew,
        };
    }

    public abstract captureSnapshot(): RegistrationSnapshot;

    // ContentLabel (0070,0080) is CS (16); the row label keeps the full LO (64) name.
    public get contentLabel(): string {
        return this.rowLabel ?? this.dicomParent.contentLabel;
    }

    public get contentDescription(): string {
        return this.dicomParent.contentDescription;
    }

    public rename(label: string): void {
        this.ensureCanModify();
        this.rowLabel = label;
        this.dicomParent.contentLabel = label;
        this.tracker?.markChanged(this);
    }

    public setDescription(description: string): void {
        this.ensureCanModify();
        this.dicomParent.contentDescription = description;
        this.tracker?.markChanged(this);
    }

    public get contentCreatorName(): string {
        return this.dicomParent.contentCreatorName;
    }

    public get contentDate(): string {
        return this.dicomParent.contentDate;
    }

    public get sopInstanceUid(): string {
        return this.dicomParent.sopInstanceUid;
    }

    public get dicomUid(): string {
        return this.sopInstanceUid;
    }

    private rowLabel: string | null = null;

    public adoptPersistedLabel(label: string): void {
        this.rowLabel = label;
        this.dicomParent.contentLabel = label;
    }

    // Dataset only: sopInstanceUid stays the session key (selection, undo) until reload.
    public adoptPersistedFileIdentity(identity: {
        readonly studyInstanceUid?: string | null;
        readonly seriesInstanceUid?: string | null;
        readonly sopInstanceUid?: string | null;
    }): void {
        const wrapper = this.dicomParent.datasetWrapper;
        if (identity.studyInstanceUid) {
            wrapper.setStudyInstanceUid(identity.studyInstanceUid);
        }
        if (identity.seriesInstanceUid) {
            wrapper.setSeriesInstanceUid(identity.seriesInstanceUid);
        }
        if (identity.sopInstanceUid) {
            wrapper.setSopInstanceUid(identity.sopInstanceUid);
        }
    }

    public get generationModel(): GenerationModelDetails | undefined {
        return this.dicomParent.generationModel;
    }

    public get isNew(): boolean {
        return this.dicomParent.isNew;
    }

    public get isExported(): boolean {
        return this.dicomParent.isExported;
    }

    public setExported(): void {
        // Mark before mutating — see DoseMatrix.setExported.
        this.tracker?.markChanged(this);
        this.dicomParent.setExported();
    }

    public get approvalStatus(): DICOMApprovalStatus {
        return this._approvalStatus;
    }

    public get isApproved(): boolean {
        return this._approvalStatus === DICOMApprovalStatus.APPROVED;
    }

    public get reviewerName(): string {
        return this._reviewerName;
    }

    public approve(reviewer: string): void {
        this._approvalStatus = DICOMApprovalStatus.APPROVED;
        this._reviewerName = reviewer;
        this.tracker?.markChanged(this);
    }

    public reject(reviewer: string): void {
        this._approvalStatus = DICOMApprovalStatus.REJECTED;
        this._reviewerName = reviewer;
        this.tracker?.markChanged(this);
    }

    public unapprove(): void {
        this._approvalStatus = DICOMApprovalStatus.UNAPPROVED;
        this._reviewerName = '';
        this.tracker?.markChanged(this);
    }

    protected ensureCanModify(): void {
        if (this._approvalStatus === DICOMApprovalStatus.APPROVED) {
            throw new ApprovedRegistrationError(
                `Cannot modify approved registration '${this.contentLabel}'`
            );
        }
    }
}
